/**
 * Phase 6b1 PR 4 — `useUserTier` hook.
 *
 * Single GET to `/api/user/tier` returns everything the
 * SubscriptionSection needs:
 *   - `tier` (free / athlete / team_25 / team_50 / team_100)
 *   - `features` (capability flags)
 *   - `isBeta` + `betaExpiresAt`
 *   - `subscriptionStatus` (active / past_due / canceled / null)
 *   - `subscriptionPeriodEnd`
 *
 * Server response shape sourced from `api/index.js:574-581`. The
 * endpoint is auth-required so unauthenticated callers get
 * `{ data: null, loading: false, error: ... }`.
 *
 * Read-only — per safety gate #4 there is no `useSetUserTier`.
 * Mutations route to vanilla `/pricing` until Phase 6h ships the
 * billing port.
 */

import { useEffect, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useAuthFetch } from './useAuthFetch';
import { sharedRequest, dropSharedRequest } from '../lib/sharedRequest';

/**
 * B129/B132 fix round (harvest 2026-09-23, critic finding 1 + security
 * finding 4) — the same bounded-read convention as `useActiveSeason.ts`'s
 * `SEASON_READ_DEADLINE_MS` (10s, a symbol race token, abandon-not-abort):
 * this GET had no deadline, so a request the server accepted and never
 * answered left `loading` true forever. `plan.tsx`'s F-511 bounce gates on
 * `!userTier.loading`, so that hang stalled the whole first-look takeover for
 * EVERY fresh athlete, not only a held one. 10s matches the house window
 * (supabase-js's own retry runs to ~9.4s on a dead connection; a shorter
 * deadline would fail a merely slow network).
 *
 * A read the deadline gives up on is a FAILED read — `error` set, `loading`
 * false — never a silent success. `data` is left as it was (nothing to throw
 * away on first load), so `coppaHeld` reads as unknown and surfaces keep
 * their refusal-time line; the server's own COPPA gate still refuses.
 */
const TIER_READ_DEADLINE_MS = 10_000;
const TIER_READ_DEADLINE = Symbol('user-tier-read-deadline');

export interface UserTierData {
  /** 'family' (CG-9): the payer OR an athlete covered by a guardian's plan. */
  tier: 'free' | 'athlete' | 'family' | 'team_25' | 'team_50' | 'team_100';
  features: Record<string, unknown>;
  isBeta: boolean;
  betaExpiresAt: string | null;
  subscriptionStatus: 'active' | 'past_due' | 'canceled' | null;
  subscriptionPeriodEnd: string | null;
  /** UAT-B2 — server-derived: a KNOWN minor (age ≤ 15, birth-fields-first,
   *  conservative rule). Drives the SubscriptionSection guardian/family branch
   *  + the Today resurface gate — never client age math. */
  derivedMinor: boolean;
  /** REL-24 — server-derived: a KNOWN age under 18. A WIDER band than
   *  `derivedMinor` (≤ 15), because the founder ruled the StoreKit buy surface
   *  minor-gated at 18. Unknown age is false here, matching the existing gate
   *  posture (REL-29 makes age deterministic at onboarding). Drives the NATIVE
   *  purchase controls only; the web Stripe surface keeps keying on
   *  `derivedMinor`, unchanged. */
  derivedUnder18: boolean;
  /** REL-24 — 'storekit' when this account's active membership was bought
   *  through Apple; null otherwise (Stripe, beta, team or family coverage),
   *  and always null while `storekit_v1` is off. The native surface uses it to
   *  decide whether to name the Apple Account as the place to manage. Never an
   *  entitlement signal. */
  subscriptionSource: 'storekit' | null;
  /** W2-FIX1 — which interval the member really pays for: 'month' or 'year',
   *  null when the server could not tell (an archived or unmapped price, no
   *  row, a failed read). The plan cards print the price for this interval and
   *  no price at all for null. Optional so a payload from an older deploy, and
   *  an existing fixture, still type-check. */
  interval?: 'month' | 'year' | null;
  /** W2-FIX1 — true when the plan is set to end at the period end (a Stripe
   *  cancellation already scheduled). The plan cards then print "Ends <date>"
   *  where they would print "Renews <date>". False when not, and always false
   *  for an Apple membership today (the server stores no auto-renew signal).
   *  Optional for the same reason as `interval`. */
  cancelAtPeriodEnd?: boolean;
  /** UAT-B2 (frame 1g) — ISO stamp of the last "ask a guardian about the
   *  Family plan" request (one open ask; 7-day re-arm). Null = none open. */
  familyAskSentAt: string | null;
  /** B129 (founder `B129: hold fact in the payload`, 2026-09-23): server-derived,
   *  true when `checkCoppaGate` holds this account for guardian consent (the
   *  under-13 COPPA hold), false when it does not. Null when the server did not
   *  send the field (an older deploy): unknown, so surfaces keep their
   *  refusal-time line. The review hold and the age wall are not this field. */
  coppaHeld?: boolean | null;
  /** B239 (founder `B239: gate the review hold too`, 2026-09-23): server-derived,
   *  true when the account is under the actual-knowledge review hold
   *  (`age_review_state = 'held'`), false when it is not. Null when the server
   *  did not send the field (an older deploy): unknown, so surfaces keep their
   *  refusal-time line. Same house pattern as `coppaHeld` above — a different
   *  hold, never the guardian-consent one. */
  ageReviewHeld?: boolean | null;
  /** B377 — present only when this account's plan is paid for by a linked
   *  parent or guardian (a guardian license). Absent or null for everyone
   *  else, and absent from an older deploy of the server. Optional so a
   *  payload without it still type-checks. */
  coverage?: {
    source: 'guardian';
    guardianFirstName: string | null;
    renewsAt: string | null;
    endsAt: string | null;
  } | null;
}

export interface UseUserTierResult {
  data: UserTierData | null;
  loading: boolean;
  error: Error | null;
}

export function useUserTier(): UseUserTierResult {
  const fetchAuthed = useAuthFetch();
  // B129/B132 fix round (security finding 4) — the shared in-flight map
  // below is MODULE-level, so an un-keyed request could hand one account's
  // `coppaHeld`/`derivedMinor` to a tree mounted for a different account
  // whose read landed inside the same in-flight window. The athlete tree
  // remounts on an in-place account switch (B180), which resets this hook's
  // own `data`/`loading` state; keying the shared request by uid closes the
  // one piece of module-level state that remount does not touch.
  const { user } = useAuthContext();
  const uid = user?.id ?? null;
  const [data, setData] = useState<UserTierData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let unmounted = false;
    setLoading(true);
    setError(null);
    // The deadline. Cleared once the read settles on time, so a normal
    // request leaves no timer behind and behaves exactly as it did before.
    let deadline: ReturnType<typeof setTimeout> | undefined;
    const deadlinePassed = new Promise<typeof TIER_READ_DEADLINE>((resolve) => {
      deadline = setTimeout(() => resolve(TIER_READ_DEADLINE), TIER_READ_DEADLINE_MS);
    });
    (async () => {
      try {
        // De-dupe: SubscriptionSection + GuardiansSection both call this hook
        // and mount together, so share one in-flight GET per page load —
        // keyed by uid so two accounts never share one entry.
        const key = uid ? `/api/user/tier:${uid}` : '/api/user/tier';
        const pending = sharedRequest(key, async () => {
          const res = await fetchAuthed('/api/user/tier', { method: 'GET' });
          if (!res.ok) throw new Error(`Server returned ${res.status}`);
          return (await res.json()) as Partial<UserTierData> | null;
        });
        const settled = await Promise.race([pending, deadlinePassed]);
        if (unmounted) return;
        if (settled === TIER_READ_DEADLINE) {
          // An abandoned read is a FAILED read (the house pattern in
          // `useActiveSeason.ts`): `data` is left exactly as it was, `error`
          // says the answer is unknown, and `loading` releases so callers
          // gated on it (the F-511 bounce, the no-season cards) proceed on
          // the refusal-time line instead of stalling forever.
          //
          // B242 (Opus sign-off finding 3, 2026-09-23) — AND THIS READ STOPS
          // ANSWERING FOR THE NEXT MOUNT. `sharedRequest` only clears its own
          // entry when the underlying fetch settles; a hung request never
          // does, so every later mount for this account used to rejoin the
          // same dead promise and pay its own 10s wait, over and over, until
          // a reload. Dropping the entry here (only if it is still THIS read
          // — `dropSharedRequest`'s own guard) means the next mount issues a
          // fresh request instead.
          dropSharedRequest(key, pending);
          setError(new Error('tier read timed out'));
          setLoading(false);
          return;
        }
        const json = settled;
        if (!json || typeof json.tier !== 'string') {
          setData(null);
        } else {
          // Defensive narrow — server may add fields; we pin the shape.
          setData({
            tier: (json.tier as UserTierData['tier']) ?? 'free',
            features:
              (json.features as Record<string, unknown> | undefined) ?? {},
            isBeta: !!json.isBeta,
            betaExpiresAt: (json.betaExpiresAt as string | null | undefined) ?? null,
            subscriptionStatus:
              (json.subscriptionStatus as UserTierData['subscriptionStatus']) ?? null,
            subscriptionPeriodEnd:
              (json.subscriptionPeriodEnd as string | null | undefined) ?? null,
            derivedMinor: json.derivedMinor === true,
            derivedUnder18: json.derivedUnder18 === true,
            subscriptionSource:
              json.subscriptionSource === 'storekit' ? 'storekit' : null,
            interval:
              json.interval === 'month' || json.interval === 'year' ? json.interval : null,
            cancelAtPeriodEnd: json.cancelAtPeriodEnd === true,
            familyAskSentAt:
              (json.familyAskSentAt as string | null | undefined) ?? null,
            coppaHeld:
              typeof json.coppaHeld === 'boolean' ? json.coppaHeld : null,
            ageReviewHeld:
              typeof json.ageReviewHeld === 'boolean' ? json.ageReviewHeld : null,
            ...(json.coverage && json.coverage.source === 'guardian'
              ? {
                  coverage: {
                    source: 'guardian' as const,
                    guardianFirstName:
                      typeof json.coverage.guardianFirstName === 'string'
                        ? json.coverage.guardianFirstName
                        : null,
                    renewsAt:
                      typeof json.coverage.renewsAt === 'string' ? json.coverage.renewsAt : null,
                    endsAt:
                      typeof json.coverage.endsAt === 'string' ? json.coverage.endsAt : null,
                  },
                }
              : {}),
          });
        }
        setLoading(false);
      } catch (e) {
        if (unmounted) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        setLoading(false);
      } finally {
        if (deadline !== undefined) clearTimeout(deadline);
      }
    })();
    return () => {
      unmounted = true;
    };
  }, [fetchAuthed, uid]);

  return { data, loading, error };
}
